import React from 'react';

const TICK = 1000;

// Time until the next whole second, so ticks land on the second rather than drifting off it.
const untilNextTick = () => TICK - (Date.now() % TICK);

/**
 * A `Date` that updates every second.
 *
 * Each tick is scheduled against the next whole second instead of on a fixed interval, so the
 * display never drifts far enough to skip one, and the time is re-read whenever the page returns
 * to the foreground — timers are throttled while a tab is hidden, so a phone coming off the lock
 * screen would otherwise show a stale time until the next tick caught up.
 */
export const useLiveTime = () => {
    const [now, setNow] = React.useState(() => new Date());

    React.useEffect(() => {
        let timeout = 0;

        const tick = () => {
            setNow(new Date());
            timeout = window.setTimeout(tick, untilNextTick());
        };
        const resync = () => {
            window.clearTimeout(timeout);
            tick();
        };

        timeout = window.setTimeout(tick, untilNextTick());
        document.addEventListener('visibilitychange', resync);
        window.addEventListener('pageshow', resync);

        return () => {
            window.clearTimeout(timeout);
            document.removeEventListener('visibilitychange', resync);
            window.removeEventListener('pageshow', resync);
        };
    }, []);

    return now;
};
